import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Dialog,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import Keypad from "../../../components/Keypad";
import { formatNumber } from "../../../utils/formatNumber";
import ModernSwal from "../../../utils/SwalUtils";
import Webcam from "react-webcam";
import { SALENG_SHOP_API_PROD } from "../../../config";
import { getAuthorizationHeaders } from "../../../services/apiClient";

const SALENG_SHOP_API = SALENG_SHOP_API_PROD;

export default function ProductScaleDialog({ open, onClose, onConfirm, init }) {

  const [weight, setWeight] = useState(0);
  const [deduct, setDeduct] = useState(0);
  const [stock, setStock] = useState(0);
  const [inputValue, setInputValue] = useState(0);
  const [dialogSide, setDialogSide] = useState("left");
  const [focused, setFocused] = useState("weight"); // "weight" | "deduct"
  const [isFirstKeypress, setIsFirstKeypress] = useState(true);
  const [snapshots, setSnapshots] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const webcamRef = useRef(null);
  const isCapturingRef = useRef(false);

  const sanitizeNumber = (s) => {
    if (s == null || s === "") return "0";
    let x = String(s).replace(/[^\d.]/g, "");
    const parts = x.split(".");
    if (parts.length > 2) x = parts[0] + "." + parts.slice(1).join("");
    if (/^0+\d/.test(x)) x = x.replace(/^0+/, "");
    return x === "" ? "0" : x;
  };

  const netNumber = useMemo(() => {
    const h = parseFloat(weight || "0");
    const l = parseFloat(deduct || "0");
    const n = h - l;
    return Number.isNaN(n) ? 0 : n;
  }, [weight, deduct]);

  const isNetNegative = netNumber < 0;

  const resetForm = () => {
    setWeight(0);
    setDeduct(0);
    setInputValue(0);
    setFocused("weight");
    setIsFirstKeypress(true);
    setSnapshots([]);
  };

  // ---------- sync จาก transactioån ----------
  useEffect(() => {
    setDialogSide(init?.dialogSide != null ? init.dialogSide : "left");
    setStock(init?.stock != null ? init.stock : 0);
    setWeight(init?.total != null ? init.total : 0);
    setDeduct(init?.rest != null ? init.rest : 0);
    setSnapshots(Array.isArray(init?.images) ? init.images : []);
  }, [init]);

  useEffect(() => {
    if (!open) return;

    navigator.mediaDevices.enumerateDevices().then((availableDevices) => {
      const videoInputs = availableDevices.filter(
        (device) => device.kind === "videoinput"
      );
      setDevices(videoInputs);

      const savedId = localStorage.getItem("preferred_camera_transform_scale");
      if (savedId && videoInputs.some((device) => device.deviceId === savedId)) {
        setSelectedDeviceId(savedId);
      } else if (videoInputs.length > 0) {
        setSelectedDeviceId(videoInputs[0].deviceId);
      }
    });
  }, [open]);

  // ---------- sync keypad ตามช่องที่โฟกัส ----------
  useEffect(() => {
    setInputValue(focused === "deduct" ? deduct || "0" : weight || "0");
  }, [focused, weight, deduct]);

  useEffect(() => {
    if (open) setIsFirstKeypress(true);
  }, [open, focused]);

  const commitLive = (nextVal) => {
    const v = sanitizeNumber(nextVal);
    setInputValue(v);
    if (focused === "weight") setWeight(v);
    if (focused === "deduct") setDeduct(v);
  };

  const captureImage = useCallback(async () => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) return null;

    const canvas = document.createElement("canvas");
    canvas.width = 1920;
    canvas.height = 1080;
    const context = canvas.getContext("2d");
    if (!context) return null;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageSrc = canvas.toDataURL("image/jpeg", 1);
    const blob = await (await fetch(imageSrc)).blob();
    return new File([blob], "transform_product.jpg", { type: "image/jpeg" });
  }, []);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "folder",
      dialogSide === "left" ? "transform_origin" : "transform_destination"
    );
    const response = await fetch(`${SALENG_SHOP_API}/files`, {
      method: "POST",
      headers: getAuthorizationHeaders(),
      body: formData,
    });
    if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`);
    return response.json();
  };

  const handleKeypadSubmit = async (result) => {
    if (isCapturingRef.current) return;
    isCapturingRef.current = true;
    setIsCapturing(true);

    try {
      const image = await captureImage();
      if (image) {
        const uploadResult = await uploadFile(image);
        if (uploadResult?.file) {
          setSnapshots((current) => [
            ...current,
            {
              url: uploadResult.file,
              at: new Date().toISOString(),
              type: focused === "deduct" ? "deduct" : "weight",
            },
          ]);
        }
      }
    } catch (error) {
      console.error("ถ่ายหรืออัปโหลดรูปแปรสภาพไม่สำเร็จ:", error);
    } finally {
      isCapturingRef.current = false;
      setIsCapturing(false);
    }

    const rawVal = parseFloat(String(result ?? "0")) || 0;
    const safeNum = Math.round((rawVal + Number.EPSILON) * 100) / 100;
    const val = String(safeNum);

    if (focused === "deduct") {
      setDeduct(val);
      setFocused("weight");
    } else {
      setWeight(val);
      setFocused("deduct");
    }
    setInputValue("0");
    setIsFirstKeypress(true);
  };

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      const key = e.key;

      if (/^[0-9]$/.test(key) || key === ".") {
        e.preventDefault();
        let next;
        if (isFirstKeypress) {
          next = key === "." ? "0." : key;
          setIsFirstKeypress(false);
        } else {
          const cur = String(inputValue ?? 0);
          if (key === "." && cur.includes(".")) return;
          if (cur.includes(".")) {
            const parts = cur.split(".");
            if (parts[1] && parts[1].length >= 2) return;
          }
          next = cur === "0" && key !== "." ? key : (cur || "") + String(key);
        }
        commitLive(next);
        return;
      }

      if (key === "Backspace") {
        e.preventDefault();
        setIsFirstKeypress(false);
        const cur = String(inputValue ?? 0);
        const next = cur.length <= 1 ? "0" : cur.slice(0, -1);
        commitLive(next);
        return;
      }

      if (key === "Escape") {
        e.preventDefault();
        commitLive("0");
        return;
      }

      if (key === "Enter") {
        e.preventDefault();
        handleKeypadSubmit(inputValue);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, inputValue, focused, isFirstKeypress]);

  const handleConfirm = async () => {
    if (stock <= 0 && dialogSide === 'left') {
      await ModernSwal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "สต็อกหมด ไม่สามารถทำรายการได้ กรุณาตรวจสอบอีกครั้ง",
        confirmButtonText: "ตกลง",
      });
      return;
    }
    if (netNumber > stock && dialogSide === 'left') {
      await ModernSwal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "จำนวนที่แปรสภาพมากกว่าสต็อกที่มีอยู่ กรุณาตรวจสอบอีกครั้ง",
        confirmButtonText: "ตกลง",
      });
      return;
    }

    onConfirm?.({
      weight: Number(sanitizeNumber(weight)),
      deduct: Number(sanitizeNumber(deduct)),
      net: netNumber,
      stock: calculateRemaining(stock, netNumber),
      images: snapshots,
    });
    resetForm();
  };

  const calculateRemaining = (stock = 0, net = 0) => {
    const result = dialogSide === "left" ? stock - net : stock + net;
    return result < 0 ? 0 : result;
  };

  const canConfirm =
    (Number(weight) > 0 || Number(deduct) > 0) && !isNetNegative;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: { borderRadius: "24px" },
        className: "shadow-2xl overflow-hidden bg-slate-50"
      }}
    >
      {/* Header */}
      <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10">
        <div>
          <h2 className="text-xl font-bold text-slate-400 mb-1">กรอกน้ำหนัก</h2>
          <div className="flex items-center gap-2">
            <span className="text-base text-slate-500 font-black">คลัง:</span>
            <span className="text-4xl font-black text-blue-600 tracking-tight">{formatNumber(stock)}</span>
            <span className="text-lg text-slate-400 font-bold">กก.</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-slate-300 hover:bg-rose-50 hover:text-rose-500 transition-colors"
        >
          <CloseIcon style={{ fontSize: 24 }} />
        </button>
      </div>

      <DialogContent className="!p-6 !overflow-hidden">
        <div className="grid grid-cols-12 gap-6 h-full">

          {/* Camera */}
          <div className="col-span-12 lg:col-span-4">
            <div className="h-full min-h-[320px] bg-black rounded-3xl overflow-hidden relative shadow-sm border border-gray-200">
              <Webcam
                ref={webcamRef}
                key={selectedDeviceId}
                audio={false}
                screenshotFormat="image/jpeg"
                className="w-full h-full object-contain"
                videoConstraints={{
                  width: 1920,
                  height: 1080,
                  deviceId: selectedDeviceId
                    ? { exact: selectedDeviceId }
                    : undefined,
                }}
              />
              <div className="absolute top-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl p-2 shadow-lg">
                <select
                  value={selectedDeviceId}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedDeviceId(value);
                    localStorage.setItem("preferred_camera_transform_scale", value);
                  }}
                  className="w-full bg-transparent text-xs font-bold outline-none"
                >
                  {devices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `กล้อง ${device.deviceId}`}
                    </option>
                  ))}
                </select>
              </div>
              {snapshots.length > 0 && (
                <div className="absolute bottom-4 right-4 bg-emerald-500 text-white text-xs font-black px-3 py-1.5 rounded-full shadow-lg">
                  บันทึกแล้ว {snapshots.length} รูป
                </div>
              )}
            </div>
          </div>

          {/* Left side: Inputs */}
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">

            {/* Weight Input Card */}
            <div
              onClick={() => {
                setFocused("weight");
                setIsFirstKeypress(true);
              }}
              className={`bg-white rounded-3xl p-5 border-2 transition-all cursor-pointer shadow-sm relative overflow-hidden group ${focused === 'weight' ? 'border-emerald-500 ring-4 ring-emerald-50' : 'border-transparent hover:border-gray-200'}`}
            >
              <div className="flex justify-between items-start mb-2">
                <span className={`text-sm font-bold uppercase tracking-wider ${focused === 'weight' ? 'text-emerald-600' : 'text-slate-400'}`}>
                  รับเข้า (รวม)
                </span>
                {focused === 'weight' && <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>}
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <span className={`text-5xl font-black ${focused === 'weight' ? 'text-slate-800' : 'text-slate-600'}`}>
                  {formatNumber(weight)}
                </span>
                <span className="text-xl font-bold text-slate-400">กก.</span>
              </div>
              {focused === 'weight' && <div className="absolute inset-0 bg-emerald-50/10 pointer-events-none"></div>}
            </div>

            {/* Deduct Input Card */}
            <div
              onClick={() => {
                setFocused("deduct");
                setIsFirstKeypress(true);
              }}
              className={`bg-white rounded-3xl p-5 border-2 transition-all cursor-pointer shadow-sm relative overflow-hidden group ${focused === 'deduct' ? 'border-red-500 ring-4 ring-red-50' : 'border-transparent hover:border-gray-200'}`}
            >
              <div className="flex justify-between items-start mb-2">
                <span className={`text-sm font-bold uppercase tracking-wider ${focused === 'deduct' ? 'text-red-500' : 'text-slate-400'}`}>
                  หักออก
                </span>
                {focused === 'deduct' && <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>}
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <span className={`text-5xl font-black ${focused === 'deduct' ? 'text-red-500' : 'text-slate-600'}`}>
                  {formatNumber(deduct)}
                </span>
                <span className="text-xl font-bold text-slate-400">กก.</span>
              </div>
              {focused === 'deduct' && <div className="absolute inset-0 bg-red-50/10 pointer-events-none"></div>}
            </div>

            <div className="border-t border-dashed border-gray-300 my-2"></div>

            {/* Result Card */}
            <div className={`rounded-3xl p-6 border transition-all ${isNetNegative ? 'bg-red-50 border-red-100' : 'bg-blue-50 border-blue-100'}`}>
              <div className="flex justify-between items-center mb-1">
                <span className={`text-sm font-bold uppercase tracking-wider ${isNetNegative ? 'text-red-500' : 'text-slate-500'}`}>
                  น้ำหนักสุทธิ
                </span>
                {isNetNegative && <span className="text-xs font-bold text-red-500 bg-red-100 px-2 py-0.5 rounded-md">ติดลบ</span>}
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <span className={`text-6xl font-black ${isNetNegative ? 'text-red-600' : 'text-blue-600'}`}>
                  {formatNumber(netNumber)}
                </span>
                <span className={`text-2xl font-bold ${isNetNegative ? 'text-red-400' : 'text-blue-400'}`}>กก.</span>
              </div>
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-blue-100/50">
                <span className="text-sm font-bold text-slate-500">ยอดคงเหลือหลังทำรายการ</span>
                <span className="text-lg font-black text-slate-700">
                  {formatNumber(calculateRemaining(stock, netNumber))} <span className="text-sm text-slate-400 font-bold">กก.</span>
                </span>
              </div>
            </div>

          </div>

          {/* Right side: Keypad */}
          <div className="col-span-12 lg:col-span-4 bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
            <Keypad
              value={inputValue}
              disabled={isCapturing}
              onChange={(val) => setInputValue(val)}
              onClear={() => {
                setInputValue("0");
                if (focused === "weight") setWeight("0");
                if (focused === "deduct") setDeduct("0");
                setIsFirstKeypress(true);
              }}
              onSubmit={handleKeypadSubmit}
            />
          </div>
        </div>
      </DialogContent>

      <div className="p-6 bg-white border-t border-gray-100">
        <button
          onClick={handleConfirm}
          disabled={!canConfirm || isCapturing}
          className="w-full h-16 bg-emerald-500 hover:bg-emerald-600 active:scale-[0.99] text-white rounded-2xl font-black text-xl shadow-lg shadow-emerald-200 disabled:opacity-50 disabled:shadow-none disabled:bg-slate-200 disabled:text-slate-400 transition-all flex items-center justify-center gap-3"
        >
          <CheckIcon style={{ fontSize: 32 }} />
          {isCapturing ? "กำลังบันทึกรูป..." : "ยืนยันรายการ"}
        </button>
      </div>
    </Dialog>
  );
}
